import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { FaAws } from 'react-icons/fa';
import { SiGooglecloud, SiOracle, SiDigitalocean, SiVultr } from 'react-icons/si';
import { LuCloud, LuServer } from 'react-icons/lu';
import { api } from '../utils/api';

// ─── Provider branding ──────────────────────────────────────────────────────

const PROVIDER_META = {
  aws:          { label: 'AWS',        color: '#FF9900', Icon: FaAws },
  azure:        { label: 'Azure',      color: '#0078D4', Icon: LuCloud },
  gcp:          { label: 'GCP',        color: '#4285F4', Icon: SiGooglecloud },
  oracle:       { label: 'Oracle',     color: '#F80000', Icon: SiOracle },
  digitalocean: { label: 'DigitalOcean', color: '#0080FF', Icon: SiDigitalocean },
  vultr:        { label: 'Vultr',      color: '#007BFC', Icon: SiVultr },
  custom:       { label: 'Custom',     color: '#A1A1AA', Icon: LuServer },
  unknown:      { label: 'Unknown',    color: '#52525B', Icon: LuCloud },
};

const getMeta = (type) => PROVIDER_META[type?.toLowerCase()] || PROVIDER_META.unknown;

// ─── Custom Tooltip ──────────────────────────────────────────────────────────

const BloombergTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const meta = getMeta(d.provider_type);
  const Icon = meta.Icon;

  return (
    <div
      className="px-4 py-3 rounded-md border"
      style={{
        background: '#0A0A0A',
        borderColor: 'rgba(255,255,255,0.10)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
        fontFamily: "'JetBrains Mono', monospace",
        minWidth: 180,
      }}
    >
      {/* Provider row */}
      <div className="flex items-center gap-2 mb-2">
        <Icon style={{ color: meta.color, width: 14, height: 14 }} />
        <span
          className="text-xs uppercase tracking-widest"
          style={{ color: meta.color, letterSpacing: '0.1em' }}
        >
          {d.provider_name}
        </span>
      </div>

      {/* Cost */}
      <div className="flex justify-between items-baseline gap-4">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Cost/mo</span>
        <span className="text-sm font-semibold text-white">
          ${d.total_cost_month.toFixed(2)}
        </span>
      </div>

      {/* Resources */}
      <div className="flex justify-between items-baseline gap-4 mt-1">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Resources</span>
        <span className="text-sm text-zinc-300">{d.resource_count}</span>
      </div>

      {/* Avg cost per resource */}
      <div className="flex justify-between items-baseline gap-4 mt-1 pt-2 border-t border-zinc-800">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">Avg / resource</span>
        <span className="text-sm text-zinc-400">
          ${d.resource_count > 0 ? (d.total_cost_month / d.resource_count).toFixed(2) : '0.00'}
        </span>
      </div>
    </div>
  );
};

// ─── Loading Skeleton ────────────────────────────────────────────────────────

const ChartSkeleton = () => (
  <div className="flex items-end justify-around gap-3 h-40 px-4 pt-6">
    {[65, 85, 40, 100, 55].map((h, i) => (
      <div
        key={i}
        className="flex-1 rounded-sm animate-pulse"
        style={{
          height: `${h}%`,
          background: 'linear-gradient(to top, #27272A, #3f3f46)',
          animationDelay: `${i * 0.1}s`,
        }}
      />
    ))}
  </div>
);

// ─── Empty State ─────────────────────────────────────────────────────────────

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-40 gap-3">
    <LuCloud className="w-10 h-10 text-zinc-700" />
    <p className="text-sm text-zinc-600">No cost data available</p>
    <p className="text-xs text-zinc-700">Connect a provider and sync resources</p>
  </div>
);

// ─── Provider Legend Row ─────────────────────────────────────────────────────

const LegendRow = ({ data }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4">
    {data.map((d) => {
      const meta = getMeta(d.provider_type);
      const Icon = meta.Icon;
      return (
        <div key={d.provider_name} className="flex items-center gap-1.5">
          <Icon style={{ color: meta.color, width: 12, height: 12 }} />
          <span
            className="text-xs uppercase"
            style={{
              color: 'rgba(161,161,170,0.7)',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.06em',
            }}
          >
            {d.provider_name}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const CostByProviderChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.getCostByProvider();
        if (mounted) setData(res.data || []);
      } catch (e) {
        if (mounted) setError('Failed to load cost data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Total across all providers
  const totalCost = data.reduce((sum, d) => sum + d.total_cost_month, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="glass-card rounded-xl p-6"
      data-testid="cost-by-provider-chart"
      style={{
        background: '#0A0A0A',
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <p
            className="text-xs uppercase tracking-widest text-zinc-500"
            style={{ letterSpacing: '0.1em' }}
          >
            Multi-Cloud Spend
          </p>
          <h2
            className="text-lg font-semibold text-zinc-200 mt-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}
          >
            Cost by Provider
          </h2>
        </div>

        {/* Total badge */}
        {!loading && data.length > 0 && (
          <div className="text-right">
            <p
              className="text-xs uppercase text-zinc-500 tracking-widest"
              style={{ letterSpacing: '0.1em' }}
            >
              Total / mo
            </p>
            <p
              className="text-xl font-semibold text-white tabular-nums"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              ${totalCost.toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {/* ── Chart body ── */}
      <div className="mt-4" style={{ height: 200 }}>
        {loading ? (
          <ChartSkeleton />
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-rose-400">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <EmptyState />
        ) : (
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={0}
            minHeight={200}
            initialDimension={{ width: 1, height: 200 }}
          >
            <BarChart
              data={data}
              margin={{ top: 20, right: 4, left: -12, bottom: 4 }}
              barCategoryGap="30%"
            >
              {/* Subtle grid lines — Bloomberg style */}
              <CartesianGrid
                vertical={false}
                stroke="rgba(255,255,255,0.05)"
                strokeDasharray="0"
              />

              <XAxis
                dataKey="provider_name"
                tick={{
                  fill: '#71717a',
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: '0.04em',
                }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                tickFormatter={(v) => `$${v}`}
                tick={{
                  fill: '#52525b',
                  fontSize: 10,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
                axisLine={false}
                tickLine={false}
                width={48}
              />

              <Tooltip
                content={<BloombergTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />

              <defs>
                {data.map((d) => {
                  const meta = getMeta(d.provider_type);
                  return (
                    <linearGradient
                      key={d.provider_name}
                      id={`grad-${d.provider_name}`}
                      x1="0" y1="0" x2="0" y2="1"
                    >
                      <stop offset="0%" stopColor={meta.color} stopOpacity={0.85} />
                      <stop offset="100%" stopColor={meta.color} stopOpacity={0.25} />
                    </linearGradient>
                  );
                })}
              </defs>

              <Bar dataKey="total_cost_month" radius={[3, 3, 0, 0]} maxBarSize={56}>
                {data.map((d) => (
                  <Cell
                    key={d.provider_name}
                    fill={`url(#grad-${d.provider_name})`}
                  />
                ))}
                {/* Inline cost label on top of each bar */}
                <LabelList
                  dataKey="total_cost_month"
                  position="top"
                  formatter={(v) => `$${v.toFixed(0)}`}
                  style={{
                    fill: '#a1a1aa',
                    fontSize: 10,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Resource count sub-row ── */}
      {!loading && data.length > 0 && (
        <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: `repeat(${data.length}, 1fr)` }}>
          {data.map((d) => {
            const meta = getMeta(d.provider_type);
            const Icon = meta.Icon;
            return (
              <div
                key={d.provider_name}
                className="flex flex-col items-center gap-1 px-2 py-2 rounded-md"
                style={{ background: `${meta.color}0D`, border: `1px solid ${meta.color}20` }}
                data-testid={`provider-stat-${d.provider_name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon style={{ color: meta.color, width: 14, height: 14 }} />
                <span
                  className="text-xs tabular-nums font-semibold"
                  style={{
                    color: meta.color,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {d.resource_count}
                </span>
                <span className="text-xs text-zinc-600 text-center leading-tight" style={{ fontSize: 9 }}>
                  resources
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Legend ── */}
      {!loading && data.length > 1 && <LegendRow data={data} />}
    </motion.div>
  );
};

export default CostByProviderChart;
